import {
  consumeSuccessorMethodContinuation,
  getCountryList,
  getCountrySubdivisionList,
  useCheckoutSession,
  useCustomerAbandonment,
  useCheckoutTracking,
  useTranslation,
  type Cart,
  type CheckoutExecution,
  type CheckoutMethod,
  type CheckoutPayerInput,
  type PaymentInteractionOutcome,
  type PaymentMethodType,
  type CountryItem,
  type CountrySubdivisionItem,
  type CheckoutSessionEntry,
} from '@checkout/core'
import {
  CheckoutPresentationRunner,
  getCheckoutPresentationRunnerDefinition,
  type CheckoutPresentationRunnerHandle,
} from '@checkout/features'
import {
  BillingInformationForm,
  createBillingInformationValue,
  hasCheckoutBillingInformationFields,
  isBillingInformationComplete,
  toCheckoutBillingAddress,
  toCheckoutPayerInput,
  useToast,
  StripeExpressCheckoutError,
} from '@checkout/ui'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { createAddPaymentInfoEvent, createPurchaseFailedEvent } from '../analytics/visivaAIEvents'
import { brandConfig } from '../config'
import { isValidEmail, useEmailValidation } from './useEmailValidation'

import type { CheckoutFormData, BillingInformationValue } from '@checkout/ui'

const CHECKOUT_SUCCESS_REDIRECT_DELAY_MS = 1500

function requiresBillingInformation(method: CheckoutMethod | null): boolean {
  return hasCheckoutBillingInformationFields(method?.checkout_fields)
}

function formatCurrency(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
}

function executionBelongsToMethod(
  execution: ReturnType<typeof useCheckoutSession>['activeExecution'],
  method: CheckoutMethod | null
): boolean {
  return Boolean(
    execution &&
    execution.selection_status === 'active' &&
    method &&
    execution.method_binding === method.method_binding
  )
}

function productReturnURL(path: string): URL {
  const normalizedPath = path.trim()
  if (
    !normalizedPath.startsWith('/') ||
    normalizedPath.startsWith('//') ||
    normalizedPath.includes('\\')
  ) {
    throw new Error('Checkout result path is invalid')
  }
  const configuredOrigin = new URL(brandConfig.productReturnOrigin)
  const result = new URL(normalizedPath, configuredOrigin)
  if (result.origin !== configuredOrigin.origin) {
    throw new Error('Checkout result path is invalid')
  }
  return result
}

function embeddedExecutionInputKey(
  sessionRevision: number,
  method: CheckoutMethod,
  customerEmail: string,
  billingAddress: Record<string, string> | undefined
): string {
  return JSON.stringify([
    sessionRevision,
    method.method_reference,
    customerEmail,
    billingAddress ?? null,
  ])
}

function currentCheckoutReturnURL(): string {
  const url = new URL(window.location.href)
  url.hash = ''
  return url.toString()
}

function outcomeExecutionNo(outcome: PaymentInteractionOutcome): string | null {
  if (!outcome.raw || typeof outcome.raw !== 'object') {
    return null
  }
  const executionNo = (outcome.raw as Record<string, unknown>).execution_no
  return typeof executionNo === 'string' && executionNo.trim() ? executionNo.trim() : null
}

export function useCheckoutPageController(
  checkoutEntry?: CheckoutSessionEntry,
  onPurchaseSuccessor?: (checkoutURL: string, billingInformation: BillingInformationValue) => void,
  initialBillingInformation?: BillingInformationValue,
  enableSuccessorContinuation = true
) {
  const { t, i18n } = useTranslation()
  const { showToast } = useToast()
  const { trackPageView, trackPayment, trackBusinessEvent } = useCheckoutTracking()
  const checkout = useCheckoutSession(
    i18n.language || brandConfig.locale.defaultLanguage,
    checkoutEntry
  )
  const emailValidation = useEmailValidation()
  const runnerRef = useRef<CheckoutPresentationRunnerHandle>(null)
  const redirectingRef = useRef(false)
  const redirectTimerRef = useRef<number | null>(null)
  const mountedRef = useRef(true)
  const hydratedSessionRef = useRef<string | null>(null)
  const hostedRedirectExecutionRef = useRef<string | null>(null)
  const selectedMethodRef = useRef<CheckoutMethod | null>(null)
  const purchaseFailureKeysRef = useRef(new Set<string>())

  const trackPurchaseFailure = useCallback(
    (
      method: CheckoutMethod,
      errorCode: 'provider_failed' | 'execution_failed',
      occurrenceKey: string
    ) => {
      if (
        brandConfig.analytics?.businessEventContract !== 'ga4_ecommerce_v1' ||
        purchaseFailureKeysRef.current.has(occurrenceKey)
      ) {
        return
      }
      purchaseFailureKeysRef.current.add(occurrenceKey)
      trackBusinessEvent(createPurchaseFailedEvent(method, errorCode))
    },
    [trackBusinessEvent]
  )

  const switchConfirmationRef = useRef<((confirmed: boolean) => void) | null>(null)
  const [switchConfirmationOpen, setSwitchConfirmationOpen] = useState(false)
  const switchSourceRef = useRef(checkout.activeExecution?.execution_no)
  switchSourceRef.current = checkout.activeExecution?.execution_no

  const resolveSwitchConfirmation = useCallback((confirmed: boolean) => {
    switchConfirmationRef.current?.(confirmed)
    switchConfirmationRef.current = null
    setSwitchConfirmationOpen(false)
  }, [])

  const paymentSwitchMessage = useCallback(
    (error: unknown, fallbackKey?: string): string => {
      const code =
        typeof error === 'string'
          ? error
          : error instanceof Error
            ? 'code' in error && typeof error.code === 'string'
              ? error.code
              : error.message
            : ''
      if (code === 'CHECKOUT_ELIGIBILITY_UNAVAILABLE') {
        return t('errors.eligibilityUnavailable')
      }
      const keys: Record<string, string> = {
        CHECKOUT_PAYMENT_SWITCH_CONFIRMATION_REQUIRED: 'confirmationRequired',
        CHECKOUT_PAYMENT_SWITCH_BLOCKED: 'blocked',
        CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED: 'queryFailed',
        CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED: 'stateChanged',
        CHECKOUT_SUCCESSOR_METHOD_UNAVAILABLE: 'successorMethodUnavailable',
        CHECKOUT_SUCCESSOR_URL_INVALID: 'successorUrlInvalid',
        CHECKOUT_PURCHASE_SUCCESSOR_PRICE_CHANGED: 'successorPriceChanged',
        CHECKOUT_PURCHASE_SUCCESSOR_UNAVAILABLE: 'successorUnavailable',
        confirmation_required: 'confirmationRequired',
        status_check_required: 'confirmationRequired',
        funds_processing: 'fundsProcessing',
        temporarily_unavailable: 'queryFailed',
        selection_inactive: 'stateChanged',
      }
      return keys[code]
        ? t(`paymentSwitch.${keys[code]}`)
        : fallbackKey
          ? t(fallbackKey)
          : error instanceof Error
            ? error.message
            : code
    },
    [t]
  )

  const [email, setEmail] = useState('')
  const [billingInformation, setBillingInformation] = useState(
    () => initialBillingInformation ?? createBillingInformationValue(checkout.countryCode)
  )
  const billingDefaultsApplied = useRef(new Set<string>())
  const billingCountryEdited = useRef(Boolean(initialBillingInformation?.country))
  const [countryOptions, setCountryOptions] = useState<CountryItem[]>([])
  const [regionOptions, setRegionOptions] = useState<CountrySubdivisionItem[]>([])
  const [countryDataError, setCountryDataError] = useState<string | null>(null)
  const [embeddedValid, setEmbeddedValid] = useState(false)
  const [providerError, setProviderError] = useState<string | null>(null)
  const [paymentTimeoutDismissed, setPaymentTimeoutDismissed] = useState(false)
  const [isAttentionReconciling, setAttentionReconciling] = useState(false)
  const [isRedirecting, setRedirecting] = useState(false)
  const [embeddedExecutionBinding, setEmbeddedExecutionBinding] = useState<{
    executionNo: string
    inputKey: string
  } | null>(null)

  const methods = checkout.methods
  const checkoutSelectedMethod = checkout.selectedMethod
  const selectCheckoutMethod = checkout.selectMethod
  const selectedMethod =
    methods.find(
      (method) => method.method_reference === checkoutSelectedMethod?.method_reference
    ) ?? null
  const activeExecution = checkout.activeExecution
  const checkoutSession = checkout.session
  const reviseCheckoutSession = checkout.revise
  const checkoutIsRevising = checkout.isRevising
  const issueResultAuthorization = checkout.issueResultAuthorization
  const terminalSessionNo = checkoutSession?.checkout_session_no
  const terminalSessionStatus = checkoutSession?.status
  const terminalResultAccessMode = checkoutSession?.result_access_mode
  const terminalWinningExecutionNo = checkoutSession?.result.winning_execution_no
  const terminalReturnPath = checkoutSession?.result.return_path
  const terminalCancelPath = checkoutSession?.result.cancel_path
  const terminalCompensationReason = checkoutSession?.result.compensation_reason
  const successRedirectMessage = t('callback.redirecting')
  const normalizedEmail = email.trim().toLowerCase()
  const executionBillingAddress = useMemo(
    () => toCheckoutBillingAddress(billingInformation, selectedMethod?.checkout_fields),
    [billingInformation, selectedMethod?.checkout_fields]
  )
  const embeddedInputKey =
    selectedMethod?.presentation_mode === 'embedded'
      ? embeddedExecutionInputKey(
          checkoutSession?.revision ?? 0,
          selectedMethod,
          normalizedEmail,
          executionBillingAddress
        )
      : null
  const activeExecutionMatchesSelection = executionBelongsToMethod(activeExecution, selectedMethod)
  const activeExecutionRequiresAction = Boolean(
    activeExecutionMatchesSelection && activeExecution?.status === 'requires_action'
  )
  const activeExecutionMatchesInput = Boolean(
    activeExecutionMatchesSelection &&
    (activeExecutionRequiresAction ||
      selectedMethod?.presentation_mode !== 'embedded' ||
      (embeddedInputKey &&
        embeddedExecutionBinding?.executionNo === activeExecution?.execution_no &&
        embeddedExecutionBinding?.inputKey === embeddedInputKey))
  )
  const activePresentation = activeExecutionMatchesInput ? activeExecution?.presentation : null
  const runnerPresentation = selectedMethod?.presentation ?? activePresentation
  const runnerDefinition = getCheckoutPresentationRunnerDefinition(runnerPresentation)
  const runnerRequiresExternalSubmit = Boolean(
    runnerDefinition && selectedMethod && runnerDefinition.requiresExternalSubmit(selectedMethod)
  )
  const runnerHidesExternalSubmit = Boolean(
    runnerDefinition && selectedMethod && runnerDefinition.hidesExternalSubmit(selectedMethod)
  )
  const shouldRenderBillingInformation = requiresBillingInformation(selectedMethod)
  const isBillingInformationValid =
    !shouldRenderBillingInformation ||
    isBillingInformationComplete(billingInformation, selectedMethod?.checkout_fields)
  const payer = useMemo<CheckoutPayerInput>(
    () => toCheckoutPayerInput(billingInformation, email, selectedMethod?.checkout_fields),
    [billingInformation, email, selectedMethod?.checkout_fields]
  )

  selectedMethodRef.current = selectedMethod

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      switchConfirmationRef.current?.(false)
      switchConfirmationRef.current = null
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      redirectingRef.current = false
      hostedRedirectExecutionRef.current = null
      setRedirecting(false)
      resolveSwitchConfirmation(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [resolveSwitchConfirmation])

  useEffect(() => {
    trackPageView('checkout')
  }, [trackPageView])

  useEffect(() => {
    if (!checkoutSelectedMethod || selectedMethod) {
      return
    }
    const fallback = methods[0]
    if (fallback) {
      selectCheckoutMethod(fallback.method_reference)
    }
  }, [checkoutSelectedMethod, methods, selectCheckoutMethod, selectedMethod])

  useEffect(() => {
    const session = checkout.session
    if (!session) return
    if (
      hydratedSessionRef.current !== session.checkout_session_no ||
      session.buyer_identity.policy === 'target_owner_locked'
    ) {
      setEmail(session.buyer_identity.email)
      hydratedSessionRef.current = session.checkout_session_no
    }
  }, [checkout.session])

  useEffect(() => {
    setBillingInformation((current) =>
      current.country ? current : { ...current, country: checkout.countryCode }
    )
  }, [checkout.countryCode])

  const defaultBillingCountry = selectedMethod?.billing_defaults?.country
  const billingDefaultsKey = `${checkoutSession?.checkout_session_no}:${checkoutSession?.buyer_identity.email}:${selectedMethod?.provider}:${selectedMethod?.pm_id}`
  useEffect(() => {
    if (!defaultBillingCountry || billingDefaultsApplied.current.has(billingDefaultsKey)) return
    billingDefaultsApplied.current.add(billingDefaultsKey)
    if (!billingCountryEdited.current) {
      setBillingInformation((current) => ({ ...current, country: defaultBillingCountry }))
    }
  }, [billingDefaultsKey, defaultBillingCountry])

  useEffect(() => {
    let active = true
    void getCountryList()
      .then((response) => {
        if (!active) {
          return
        }
        if (response.error || !response.data) {
          throw new Error(response.error?.message || 'Billing countries are unavailable')
        }
        setCountryOptions(response.data)
        setCountryDataError(null)
      })
      .catch((error: unknown) => {
        if (active) {
          setCountryDataError(
            error instanceof Error ? error.message : 'Billing countries are unavailable'
          )
        }
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const country = billingInformation.country.trim().toUpperCase()
    const regionCapability = selectedMethod?.checkout_fields.billing_region
    if (!country || regionCapability?.collection !== 'checkout_form') {
      setRegionOptions([])
      return
    }
    let active = true
    void getCountrySubdivisionList(country)
      .then((response) => {
        if (!active) {
          return
        }
        if (response.error) {
          throw new Error(response.error.message)
        }
        setRegionOptions(response.data ?? [])
      })
      .catch((error: unknown) => {
        if (active) {
          setRegionOptions([])
          setCountryDataError(
            error instanceof Error ? error.message : 'Billing regions are unavailable'
          )
        }
      })
    return () => {
      active = false
    }
  }, [billingInformation.country, selectedMethod?.checkout_fields.billing_region])

  useEffect(() => {
    setEmbeddedValid(false)
    setProviderError(null)
  }, [activeExecution?.execution_no, selectedMethod?.method_reference])

  useEffect(() => {
    if (!activeExecutionMatchesSelection || !activeExecution?.execution_no || !embeddedInputKey) {
      return
    }
    setEmbeddedExecutionBinding((current) => {
      if (current?.executionNo === activeExecution.execution_no) {
        return current
      }
      return {
        executionNo: activeExecution.execution_no,
        inputKey: embeddedInputKey,
      }
    })
  }, [activeExecution?.execution_no, activeExecutionMatchesSelection, embeddedInputKey])

  useEffect(() => {
    if (!checkout.paymentStatusTimedOut) {
      setPaymentTimeoutDismissed(false)
    }
  }, [checkout.paymentStatusTimedOut])

  useEffect(() => {
    if (
      !terminalSessionNo ||
      !terminalSessionStatus ||
      redirectingRef.current ||
      ((checkout.hasPendingRestart || checkout.isRestarting) &&
        terminalSessionStatus !== 'completed') ||
      !['completed', 'compensated', 'canceled', 'expired'].includes(terminalSessionStatus)
    ) {
      return
    }
    redirectingRef.current = true
    setRedirecting(true)
    const redirect = async () => {
      const path = terminalSessionStatus === 'completed' ? terminalReturnPath : terminalCancelPath
      if (!path) {
        throw new Error('Checkout result path is unavailable')
      }
      const url = productReturnURL(path)
      url.searchParams.set('checkout_session_no', terminalSessionNo)
      url.searchParams.set('checkout_status', terminalSessionStatus)
      if (terminalCompensationReason) {
        url.searchParams.set('checkout_reason', terminalCompensationReason)
      }
      if (terminalResultAccessMode === 'handoff') {
        const authorizationCode = await issueResultAuthorization()
        if (!mountedRef.current) return
        url.hash = new URLSearchParams({ authorization_code: authorizationCode }).toString()
      }
      const navigate = () => window.location.replace(url.toString())
      // 会话已被服务端判定为 completed，本身就是权威成功事实，无需渠道回传结果参数。
      const shouldAnnouncePaymentSuccess = Boolean(
        terminalSessionStatus === 'completed' &&
        checkout.entry.returnedExecutionNo &&
        terminalWinningExecutionNo === checkout.entry.returnedExecutionNo
      )
      if (shouldAnnouncePaymentSuccess) {
        showToast('success', successRedirectMessage)
        redirectTimerRef.current = window.setTimeout(() => {
          redirectTimerRef.current = null
          if (mountedRef.current) {
            navigate()
          }
        }, CHECKOUT_SUCCESS_REDIRECT_DELAY_MS)
        return
      }
      navigate()
    }
    void redirect().catch((error: unknown) => {
      if (!mountedRef.current) return
      redirectingRef.current = false
      setRedirecting(false)
      setProviderError(error instanceof Error ? error.message : 'Checkout result is unavailable')
    })
  }, [
    checkout.entry.returnedExecutionNo,
    checkout.hasPendingRestart,
    checkout.isRestarting,
    issueResultAuthorization,
    showToast,
    successRedirectMessage,
    terminalCancelPath,
    terminalCompensationReason,
    terminalResultAccessMode,
    terminalReturnPath,
    terminalSessionNo,
    terminalSessionStatus,
    terminalWinningExecutionNo,
  ])

  const uiPaymentMethods = methods.map((method) => ({
    type: method.method_reference as PaymentMethodType,
    pmId: method.pm_id,
    analyticsKey: `${method.provider}_${method.pm_id}`,
    label: method.name,
    icon: method.logo || undefined,
    available: true,
    provider: method.provider,
  }))

  const amountMethod = selectedMethod ?? methods[0] ?? null
  const quantity = checkout.session?.pricing_input.quantity ?? 1
  const currency = checkout.session?.pricing_input.currency ?? brandConfig.locale.defaultCurrency
  const currencyOptions = checkout.currencyOptions
  const sessionBuyerEmail = checkout.session?.buyer_identity.email.trim().toLowerCase() ?? ''
  const emailReadOnly =
    checkout.session?.buyer_identity.policy === 'target_owner_locked' || checkout.isRevising
  const buyerIdentityDirty = Boolean(
    checkout.session?.buyer_identity.policy === 'editable' &&
    isValidEmail(normalizedEmail) &&
    normalizedEmail !== sessionBuyerEmail
  )

  useEffect(() => {
    const session = checkoutSession
    if (
      !session ||
      checkoutIsRevising ||
      session.buyer_identity.policy !== 'editable' ||
      !isValidEmail(normalizedEmail) ||
      normalizedEmail === session.buyer_identity.email.trim().toLowerCase()
    ) {
      return
    }
    const timer = window.setTimeout(() => {
      void reviseCheckoutSession(
        session.pricing_input.currency,
        session.pricing_input.quantity,
        session.pricing_input.coupon_code,
        normalizedEmail
      ).catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Buyer email could not be updated'
        setProviderError(message)
        showToast('error', message)
      })
    }, 500)
    return () => window.clearTimeout(timer)
  }, [checkoutIsRevising, checkoutSession, normalizedEmail, reviseCheckoutSession, showToast])
  const cart = useMemo<Cart>(() => {
    const breakdown =
      amountMethod?.amount_breakdown ?? checkout.session?.pricing_input.amount_breakdown
    if (!checkout.session || !breakdown || Array.isArray(breakdown)) {
      return {
        items: [],
        subtotal: 0,
        tax: 0,
        discount: 0,
        total: 0,
        currency,
      }
    }
    const sessionCurrency = checkout.session.pricing_input.currency
    return {
      items: [
        {
          id: checkout.session.target.reference,
          name: checkout.session.target.name,
          quantity: breakdown.quantity,
          unitPrice: Number(breakdown.unit_amount),
          currency: sessionCurrency,
        },
      ],
      subtotal: Number(breakdown.subtotal),
      tax: Number(breakdown.tax_amount),
      discount: Number(breakdown.discount_amount),
      discountCode: checkout.session.pricing_input.coupon_code ?? undefined,
      total: Number(breakdown.total_amount),
      currency: sessionCurrency,
    }
  }, [amountMethod, checkout.session, currency])

  const pricingUnavailable = Boolean(checkout.session && cart.items.length === 0)

  const handleEmailChange = useCallback(
    (nextEmail: string) => {
      setEmail(nextEmail)
      emailValidation.handleChange(nextEmail)
    },
    [emailValidation]
  )

  const handleEmailBlur = useCallback(() => {
    emailValidation.handleBlur(email)
  }, [email, emailValidation])

  const handlePaymentMethodSelect = useCallback(
    (methodReference: PaymentMethodType) => {
      const target = checkout.methods.find((method) => method.method_reference === methodReference)
      if (!target) return
      resolveSwitchConfirmation(false)
      checkout.selectMethod(methodReference)
    },
    [checkout, resolveSwitchConfirmation]
  )

  const handleProviderOutcome = useCallback(
    (outcome: PaymentInteractionOutcome) => {
      if (outcome.status === 'failed') {
        const currentMethod = selectedMethodRef.current
        if (currentMethod && outcome.errorCode !== 'CHECKOUT_ELIGIBILITY_UNAVAILABLE') {
          const occurrenceKey =
            outcomeExecutionNo(outcome) ??
            activeExecution?.execution_no ??
            currentMethod.method_reference
          trackPurchaseFailure(currentMethod, 'provider_failed', `provider:${occurrenceKey}`)
        }
        setProviderError(outcome.error)
        showToast('error', outcome.error)
        void checkout.refresh()
        return
      }
      setProviderError(null)
      const executionNo = outcomeExecutionNo(outcome) ?? activeExecution?.execution_no
      if ((outcome.status === 'succeeded' || outcome.status === 'processing') && executionNo) {
        void checkout.reconcileExecution(executionNo).catch((error: unknown) => {
          const message = error instanceof Error ? error.message : 'Payment status is unavailable'
          setProviderError(message)
          showToast('error', message)
        })
      } else {
        void checkout.refresh()
      }
    },
    [activeExecution?.execution_no, checkout, showToast, trackPurchaseFailure]
  )

  const launchHostedExecution = useCallback((execution: CheckoutExecution): boolean => {
    if (
      execution.presentation?.flow !== 'hosted_redirect' ||
      !execution.presentation.checkout_url
    ) {
      return false
    }
    hostedRedirectExecutionRef.current = null
    redirectingRef.current = true
    setRedirecting(true)
    window.location.assign(execution.presentation.checkout_url)
    return true
  }, [])

  useEffect(() => {
    if (activeExecution && hostedRedirectExecutionRef.current === activeExecution.execution_no) {
      launchHostedExecution(activeExecution)
    }
  }, [activeExecution, launchHostedExecution])

  const startSelectedExecution = useCallback(
    async (
      expectedMethodReference: string,
      providerClientPayload?: Record<string, unknown>,
      unavailablePayPalExecutionNo?: string
    ): Promise<CheckoutExecution | null> => {
      const currentMethod = selectedMethodRef.current
      if (!currentMethod || currentMethod.method_reference !== expectedMethodReference) {
        throw new Error('Checkout payment method changed')
      }
      if (!selectedMethod || selectedMethod.method_reference !== expectedMethodReference) {
        throw new Error('Checkout method is unavailable')
      }
      if (!emailValidation.validate(email)) {
        return null
      }
      if (buyerIdentityDirty || checkout.isRevising) {
        return null
      }
      if (!isBillingInformationValid) {
        showToast('error', t('validation.required'))
        return null
      }
      if (selectedMethod.requires_provider_client_payload && !providerClientPayload) {
        throw new Error('Provider client payload is required for the selected method')
      }
      if (!selectedMethod.requires_provider_client_payload && providerClientPayload) {
        throw new Error('Provider client payload is not accepted for the selected method')
      }
      const paymentSwitch = checkout.paymentSwitch
      if (paymentSwitch?.mode === 'blocked') {
        throw new Error(paymentSwitchMessage(paymentSwitch.reason, 'paymentSwitch.blocked'))
      }
      if (paymentSwitch?.mode === 'confirm_unpaid' && !unavailablePayPalExecutionNo) {
        if (switchConfirmationRef.current) return null
        const confirmed = await new Promise<boolean>((resolve) => {
          switchConfirmationRef.current = resolve
          setSwitchConfirmationOpen(true)
        })
        if (!confirmed) return null
        if (
          selectedMethodRef.current?.method_reference !== expectedMethodReference ||
          switchSourceRef.current !== paymentSwitch.executionNo
        ) {
          throw new Error(paymentSwitchMessage('CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED'))
        }
      }
      if (paymentSwitch?.mode === 'purchase_successor') {
        const successor = await checkout.createPurchaseSuccessor(
          paymentSwitch.executionNo,
          'selection_changed'
        )
        if (!onPurchaseSuccessor) throw new Error('CHECKOUT_SUCCESSOR_URL_INVALID')
        onPurchaseSuccessor(successor.checkout_url, billingInformation)
        return null
      }
      trackPayment('payment_submit_start', {
        payment_provider: selectedMethod.provider,
        payment_method_id: selectedMethod.pm_id,
      })
      if (checkout.session && brandConfig.analytics?.businessEventContract === 'ga4_ecommerce_v1') {
        const addPaymentInfoEvent = createAddPaymentInfoEvent(checkout.session, selectedMethod)
        if (addPaymentInfoEvent) trackBusinessEvent(addPaymentInfoEvent)
      }
      const execution = await checkout
        .startExecution({
          ...(unavailablePayPalExecutionNo
            ? {
                abandon_previous: {
                  execution_no: unavailablePayPalExecutionNo,
                  approval_link_unavailable: true,
                },
              }
            : paymentSwitch
              ? {
                  abandon_previous: {
                    execution_no: paymentSwitch.executionNo,
                    confirmed_unpaid: true,
                  },
                }
              : {}),
          customer_email: email.trim().toLowerCase(),
          billing_address: toCheckoutBillingAddress(
            billingInformation,
            selectedMethod.checkout_fields
          ),
          client_context: {
            locale: i18n.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          ...(providerClientPayload
            ? { provider_client_payload: providerClientPayload }
            : undefined),
        })
        .catch((error: unknown) => {
          if (
            error instanceof Error &&
            'code' in error &&
            error.code === 'CHECKOUT_ELIGIBILITY_UNAVAILABLE'
          ) {
            throw new StripeExpressCheckoutError(
              paymentSwitchMessage(error),
              'CHECKOUT_ELIGIBILITY_UNAVAILABLE',
              error
            )
          }
          throw new Error(paymentSwitchMessage(error))
        })
      if (['failed', 'canceled', 'expired'].includes(execution.status)) {
        trackPurchaseFailure(
          selectedMethod,
          'execution_failed',
          `execution:${execution.execution_no}`
        )
        throw new Error(t('toast.paymentFailedTryOther'))
      }
      if (selectedMethod.presentation_mode === 'hosted_redirect') {
        hostedRedirectExecutionRef.current = execution.execution_no
        launchHostedExecution(execution)
      }
      return execution
    },
    [
      billingInformation,
      buyerIdentityDirty,
      checkout,
      email,
      emailValidation,
      i18n.language,
      isBillingInformationValid,
      launchHostedExecution,
      paymentSwitchMessage,
      onPurchaseSuccessor,
      selectedMethod,
      showToast,
      t,
      trackPayment,
      trackBusinessEvent,
      trackPurchaseFailure,
    ]
  )

  const canStartPresentationExecution = useCallback(
    (expectedMethodReference: string): boolean => {
      if (selectedMethodRef.current?.method_reference !== expectedMethodReference) {
        return false
      }
      if (!emailValidation.validate(email) || buyerIdentityDirty || checkout.isRevising) {
        return false
      }
      if (!isBillingInformationValid) {
        showToast('error', t('validation.required'))
        return false
      }
      if (activeExecutionMatchesSelection && activeExecution?.status === 'requires_action') {
        return false
      }
      return !checkout.isStartingExecution
    },
    [
      buyerIdentityDirty,
      activeExecution?.status,
      activeExecutionMatchesSelection,
      checkout.isRevising,
      checkout.isStartingExecution,
      email,
      emailValidation,
      isBillingInformationValid,
      showToast,
      t,
    ]
  )

  const startPresentationExecution = useCallback(
    async (
      expectedMethodReference: string,
      providerClientPayload?: Record<string, unknown>
    ): Promise<CheckoutExecution | null> => {
      const execution = await startSelectedExecution(expectedMethodReference, providerClientPayload)
      if (!execution) return null
      if (embeddedInputKey) {
        setEmbeddedExecutionBinding({
          executionNo: execution.execution_no,
          inputKey: embeddedInputKey,
        })
      }
      return execution
    },
    [embeddedInputKey, startSelectedExecution]
  )

  const handleFormSubmit = useCallback(
    async (_data: CheckoutFormData) => {
      setProviderError(null)
      try {
        if (!selectedMethod) {
          throw new Error('Checkout method is unavailable')
        }
        if (
          activeExecution?.provider === 'paypal' &&
          activeExecution.selection_status === 'active' &&
          activeExecutionMatchesSelection &&
          activeExecution.payment_switch?.mode === 'purchase_successor'
        ) {
          if (activeExecution.status !== 'requires_action') {
            throw new Error('CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED')
          }
          if (activeExecution.presentation !== null) {
            try {
              const verified = await checkout.reconcileExecution(activeExecution.execution_no, true)
              if (verified.status !== 'requires_action') return
              if (launchHostedExecution(verified)) return
              if (verified.presentation !== null) {
                throw new Error('CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED')
              }
            } catch (error) {
              const code =
                typeof error === 'object' && error !== null && 'code' in error ? error.code : null
              if (code === 'CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED') {
                await checkout.refresh()
                return
              }
              if (
                code === 'CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED' &&
                launchHostedExecution(activeExecution)
              ) {
                return
              }
              if (code !== 'CHECKOUT_APPROVAL_LINK_UNAVAILABLE') throw error
            }
          }
          const successor = await checkout.createPurchaseSuccessor(
            activeExecution.execution_no,
            'link_unavailable'
          )
          if (!onPurchaseSuccessor) throw new Error('CHECKOUT_SUCCESSOR_URL_INVALID')
          onPurchaseSuccessor(successor.checkout_url, billingInformation)
          return
        }
        if (
          activeExecution?.provider === 'paypal' &&
          activeExecution.selection_status === 'active' &&
          activeExecutionMatchesSelection
        ) {
          let verified: CheckoutExecution
          try {
            verified = await checkout.reconcileExecution(activeExecution.execution_no, true)
          } catch (error) {
            if (
              error instanceof Error &&
              'code' in error &&
              error.code === 'CHECKOUT_APPROVAL_LINK_UNAVAILABLE'
            ) {
              await startSelectedExecution(
                selectedMethod.method_reference,
                undefined,
                activeExecution.execution_no
              )
              return
            }
            if (
              error instanceof Error &&
              'code' in error &&
              error.code === 'CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED'
            ) {
              await checkout.refresh()
              return
            }
            throw error
          }
          if (
            verified.execution_no !== activeExecution.execution_no ||
            verified.provider !== 'paypal'
          ) {
            throw new Error('CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED')
          }
          if (verified.status === 'succeeded') return
          if (['failed', 'canceled', 'expired'].includes(verified.status)) {
            await startSelectedExecution(selectedMethod.method_reference)
            return
          }
          if (verified.status !== 'requires_action') {
            throw new Error('CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED')
          }
          if (launchHostedExecution(verified)) return
          await startSelectedExecution(
            selectedMethod.method_reference,
            undefined,
            activeExecution.execution_no
          )
          return
        }
        if (runnerRequiresExternalSubmit) {
          await runnerRef.current?.submit()
          return
        }
        if (
          activeExecutionRequiresAction &&
          activeExecution &&
          launchHostedExecution(activeExecution)
        ) {
          return
        }
        await startSelectedExecution(selectedMethod.method_reference)
      } catch (error) {
        const message = paymentSwitchMessage(error) || t('payment.error')
        setProviderError(message)
        showToast('error', message)
      }
    },
    [
      activeExecution,
      activeExecutionMatchesSelection,
      activeExecutionRequiresAction,
      billingInformation,
      checkout,
      launchHostedExecution,
      runnerRequiresExternalSubmit,
      paymentSwitchMessage,
      onPurchaseSuccessor,
      t,
      selectedMethod,
      showToast,
      startSelectedExecution,
    ]
  )

  useEffect(() => {
    if (
      !enableSuccessorContinuation ||
      checkout.isInitializing ||
      checkout.isLoadingMethods ||
      checkout.isStartingExecution ||
      checkout.isRevising ||
      checkout.activeExecution !== null ||
      !checkout.session ||
      checkout.session.status !== 'open' ||
      !selectedMethod ||
      buyerIdentityDirty ||
      email.trim().toLowerCase() !== checkout.session.buyer_identity.email.trim().toLowerCase()
    ) {
      return
    }
    if (!consumeSuccessorMethodContinuation(checkout.entry.sessionNo, selectedMethod)) return
    if (
      selectedMethod.requires_provider_client_payload ||
      runnerRequiresExternalSubmit ||
      !isBillingInformationValid
    ) {
      return
    }
    void startSelectedExecution(selectedMethod.method_reference).catch((error: unknown) => {
      const message = paymentSwitchMessage(error) || t('payment.error')
      setProviderError(message)
      showToast('error', message)
    })
  }, [
    buyerIdentityDirty,
    enableSuccessorContinuation,
    checkout.entry.sessionNo,
    checkout.activeExecution,
    checkout.isInitializing,
    checkout.isLoadingMethods,
    checkout.isRevising,
    checkout.isStartingExecution,
    checkout.session,
    email,
    isBillingInformationValid,
    paymentSwitchMessage,
    runnerRequiresExternalSubmit,
    selectedMethod,
    showToast,
    startSelectedExecution,
    t,
  ])

  const completeNoPayment = useCallback(async () => {
    try {
      await checkout.complete()
    } catch (error) {
      const message = paymentSwitchMessage(error) || t('payment.error')
      setProviderError(message)
      showToast('error', message)
    }
  }, [checkout, paymentSwitchMessage, showToast, t])

  const reconcileAttention = useCallback(async () => {
    setProviderError(null)
    setAttentionReconciling(true)
    try {
      if (activeExecution?.execution_no) {
        await checkout.reconcileExecution(activeExecution.execution_no)
      } else {
        await checkout.refresh()
      }
    } catch {
      const message = t('modal.paymentTimeout.message')
      setProviderError(message)
      showToast('error', message)
    } finally {
      setAttentionReconciling(false)
    }
  }, [activeExecution?.execution_no, checkout, showToast, t])

  const applyCoupon = useCallback(
    async (code: string) => {
      try {
        await checkout.revise(currency, quantity, code.trim() || null, normalizedEmail)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Coupon could not be applied'
        setProviderError(message)
        showToast('error', message)
      }
    },
    [checkout, currency, normalizedEmail, quantity, showToast]
  )

  const handleCurrencyChange = useCallback(
    async (nextCurrency: string) => {
      if (!checkout.session || checkout.isRevising || nextCurrency === currency) {
        return
      }
      const customerEmail = isValidEmail(normalizedEmail) ? normalizedEmail : sessionBuyerEmail
      try {
        await checkout.revise(
          nextCurrency,
          quantity,
          checkout.session.pricing_input.coupon_code,
          customerEmail
        )
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Currency could not be updated'
        setProviderError(message)
        showToast('error', message)
      }
    },
    [checkout, currency, normalizedEmail, quantity, sessionBuyerEmail, showToast]
  )

  const embeddedPaymentSection: ReactNode =
    runnerDefinition && runnerPresentation && selectedMethod ? (
      <CheckoutPresentationRunner
        ref={runnerRef}
        execution={activeExecutionMatchesInput ? activeExecution : null}
        method={selectedMethod}
        methodPresentation={selectedMethod.presentation}
        payer={payer}
        countryCode={billingInformation.country || checkout.countryCode}
        merchantName={brandConfig.name}
        returnUrl={currentCheckoutReturnURL()}
        isStartingExecution={checkout.isStartingExecution}
        canStartExecution={canStartPresentationExecution}
        startExecution={startPresentationExecution}
        onOutcome={handleProviderOutcome}
        onValidityChange={setEmbeddedValid}
      />
    ) : undefined

  const billingInformationSection = shouldRenderBillingInformation ? (
    <BillingInformationForm
      value={billingInformation}
      onChange={(next) => {
        if (next.country !== billingInformation.country) billingCountryEdited.current = true
        setBillingInformation(next)
      }}
      checkoutFields={selectedMethod?.checkout_fields}
      countryOptions={countryOptions}
      regionOptions={regionOptions}
      disabled={checkout.isStartingExecution}
    />
  ) : undefined

  const resolveText = useCallback(
    (value: string | undefined, fallbackKey: string): string => {
      if (!value) return t(fallbackKey)
      return value.includes('.') ? t(value) : value
    },
    [t]
  )

  const renderTermsText = useCallback(() => {
    const template = resolveText(brandConfig.termsText?.template, 'terms.agreement')
    const privacyLabel = resolveText(brandConfig.termsText?.privacyLinkText, 'terms.privacyPolicy')
    const refundLabel = resolveText(brandConfig.termsText?.refundLinkText, 'terms.refundPolicy')
    const content = template.split(/(\{privacyLink\}|\{refundLink\})/).map((part, index) => {
      if (part === '{privacyLink}') {
        return (
          <a key={index} href={brandConfig.legal.privacyUrl} target="_blank" rel="noreferrer">
            {privacyLabel}
          </a>
        )
      }
      if (part === '{refundLink}') {
        return (
          <a
            key={index}
            href={brandConfig.legal.refundPolicyUrl ?? brandConfig.legal.termsUrl}
            target="_blank"
            rel="noreferrer"
          >
            {refundLabel}
          </a>
        )
      }
      return part
    })
    const cryptocurrencyNotice = brandConfig.termsText?.cryptocurrencyNotice
    if (selectedMethod?.payment_type !== 'cryptocurrency' || !cryptocurrencyNotice) {
      return content
    }

    if (!brandConfig.legal.refundPolicyUrl) {
      throw new Error('Cryptocurrency notice requires a refund policy URL')
    }
    const refundPolicyURL = new URL(
      brandConfig.legal.refundPolicyUrl,
      brandConfig.productReturnOrigin
    )
    refundPolicyURL.hash = cryptocurrencyNotice.refundPolicySectionId
    const noticeTemplate = resolveText(
      cryptocurrencyNotice.template,
      'terms.visiva.cryptocurrencyNotice'
    )
    const cryptocurrencyLinkLabel = resolveText(
      cryptocurrencyNotice.refundLinkText,
      'terms.visiva.cryptocurrencyPayments'
    )
    const notice = noticeTemplate.split(/(\{refundLink\})/).map((part, index) =>
      part === '{refundLink}' ? (
        <a key={index} href={refundPolicyURL.toString()} target="_blank" rel="noreferrer">
          {cryptocurrencyLinkLabel}
        </a>
      ) : (
        part
      )
    )

    return (
      <>
        <span>{notice}</span>
        <br />
        {content}
      </>
    )
  }, [resolveText, selectedMethod?.payment_type])

  const footerLinks = (brandConfig.footer?.links ?? []).map((link) => ({
    ...link,
    label: link.label.includes('.') ? t(link.label) : link.label,
  }))
  const disclaimerTitle = resolveText(brandConfig.footer?.disclaimerTitle, 'footer.disclaimerTitle')
  const disclaimer = resolveText(brandConfig.footer?.disclaimer, 'footer.disclaimer')
  const copyright = resolveText(brandConfig.footer?.copyrightTemplate, 'footer.copyright')
    .replace('{year}', new Date().getFullYear().toString())
    .replace('{brandName}', brandConfig.name)

  const sessionStatus = checkout.session?.status
  // 用户已从渠道回跳且服务端尚未给出终态，此时处于对账确认中。
  const isAwaitingReturnConfirmation = Boolean(
    checkout.entry.returnedExecutionNo &&
    !checkout.entry.cancellationRequested &&
    activeExecution?.execution_no === checkout.entry.returnedExecutionNo &&
    !['failed', 'canceled', 'expired'].includes(activeExecution.status) &&
    sessionStatus !== 'requires_attention' &&
    sessionStatus &&
    !['completed', 'compensated', 'canceled', 'expired'].includes(sessionStatus)
  )
  const processingPending = Boolean(
    sessionStatus !== 'requires_attention' &&
    !checkout.paymentStatusTimedOut &&
    (isRedirecting ||
      isAwaitingReturnConfirmation ||
      (!(
        checkout.entry.cancellationRequested &&
        activeExecution?.provider === 'paypal' &&
        activeExecution?.execution_no === checkout.entry.returnedExecutionNo
      ) &&
        activeExecutionMatchesSelection &&
        activeExecution &&
        !activeExecution.payment_switch &&
        !(
          activeExecution.presentation?.flow === 'hosted_redirect' &&
          activeExecution.presentation.checkout_url
        ) &&
        [
          'prepared',
          'dispatch_pending',
          'processing',
          'cancel_requested',
          'canceling',
          'unknown',
        ].includes(activeExecution.status)))
  )
  const abandonment = useCustomerAbandonment(checkoutSession, processingPending)
  const customerAbandonment = {
    ...abandonment,
    canRestart: abandonment.canRestart || checkout.hasPendingRestart,
  }
  const isPaymentProcessing =
    processingPending && !customerAbandonment.waitElapsed && !checkout.hasPendingRestart
  const step =
    sessionStatus === 'completed'
      ? 'complete'
      : sessionStatus === 'requires_attention'
        ? 'attention'
        : isPaymentProcessing
          ? 'processing'
          : 'payment'
  const terminalExecutionError =
    activeExecutionMatchesSelection &&
    activeExecution &&
    ['failed', 'canceled', 'expired'].includes(activeExecution.status)
      ? t('toast.paymentFailedTryOther')
      : null
  const formError =
    (checkout.error ? paymentSwitchMessage(checkout.error) : null) ||
    providerError ||
    (checkout.paymentSwitch?.mode === 'blocked'
      ? paymentSwitchMessage(checkout.paymentSwitch.reason, 'paymentSwitch.blocked')
      : null) ||
    countryDataError ||
    terminalExecutionError ||
    (sessionStatus === 'requires_attention' ? t('modal.paymentTimeout.message') : null)
  return {
    isReady:
      !checkout.isInitializing &&
      !checkout.isLoadingMethods &&
      Boolean(checkout.session) &&
      email.trim().toLowerCase() === sessionBuyerEmail,
    loadError: !checkout.isInitializing && !checkout.session ? checkout.error : null,
    brandConfig,
    cart,
    step,
    currentCurrency: currency,
    currencyOptions,
    productLoading: checkout.isInitializing || (pricingUnavailable && checkout.isLoadingMethods),
    productError: checkout.error
      ? paymentSwitchMessage(checkout.error)
      : pricingUnavailable && !checkout.isLoadingMethods
        ? t('orderSummary.priceUnavailable')
        : null,
    paymentMethodsLoading: checkout.isLoadingMethods,
    selectedPaymentMethod: selectedMethod
      ? (selectedMethod.method_reference as PaymentMethodType)
      : null,
    uiPaymentMethods,
    email,
    emailReadOnly,
    emailValidation,
    couponEnabled:
      brandConfig.features.couponCode && checkout.session?.target.type === 'catalog_price',
    currencySelectorEnabled:
      brandConfig.features.showCurrencySelector === true && currencyOptions.length > 1,
    applyCoupon,
    handleCurrencyChange,
    formatPrice: (amount: number, currencyCode: string) =>
      formatCurrency(amount, currencyCode, i18n.language),
    handleEmailChange,
    handleEmailBlur,
    handlePaymentMethodSelect,
    handleFormSubmit,
    submitLabelKey: 'payment.subscribe',
    formError,
    embeddedPaymentSection,
    billingInformationSection,
    payer,
    isSubmitting:
      checkout.isStartingExecution ||
      checkout.isRevising ||
      checkout.isRestarting ||
      buyerIdentityDirty,
    isPaymentProcessing,
    customerAbandonment,
    paymentSwitchConfirmation: {
      open: switchConfirmationOpen,
      titleKey: 'paymentSwitch.title',
      messageKey:
        checkout.paymentSwitch?.mode === 'purchase_successor'
          ? 'paymentSwitch.purchaseSuccessorMessage'
          : 'paymentSwitch.message',
      confirmKey:
        checkout.paymentSwitch?.mode === 'purchase_successor'
          ? 'paymentSwitch.purchaseSuccessorConfirm'
          : 'paymentSwitch.confirm',
      confirm: () => resolveSwitchConfirmation(true),
      close: () => resolveSwitchConfirmation(false),
    },
    paymentTimeout: {
      open: checkout.paymentStatusTimedOut && !paymentTimeoutDismissed,
      retry: () => {
        setPaymentTimeoutDismissed(false)
        checkout.retryPaymentStatusPolling()
      },
      close: () => setPaymentTimeoutDismissed(true),
      supportHref:
        (brandConfig.contact?.email ? `mailto:${brandConfig.contact.email}` : null) ||
        brandConfig.contact?.supportUrl ||
        null,
    },
    attention: {
      retry: reconcileAttention,
      isRetrying: isAttentionReconciling,
      supportHref:
        (brandConfig.contact?.email ? `mailto:${brandConfig.contact.email}` : null) ||
        brandConfig.contact?.supportUrl ||
        null,
    },
    embeddedPaymentSectionVariant: runnerDefinition?.variant ?? ('panel' as const),
    hideSubmitButton: runnerHidesExternalSubmit,
    isEmbeddedPaymentValid: !runnerRequiresExternalSubmit || embeddedValid,
    isBillingInformationValid,
    renderTermsText,
    footerLinks,
    disclaimerTitle,
    disclaimer,
    copyright,
    noPaymentCompletionAvailable:
      checkout.session?.status === 'open' &&
      checkout.session.available_actions.includes('complete_without_payment'),
    completeNoPayment,
  }
}
